import React from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { ROLES_TYPE } from 'utils'
import Logout from '../pages/login/Logout'
import RootLayout from '../layouts/RootLayout'
import AuthLayout from '../layouts/AuthLayout'
import AuthGuard from './AuthGuard'
import GuestGuard from './GuestGuard'
import RouteGuard from './RouteGuard'
import RouteErrorBoundary from '../shared/molecules/networkErrorModal/RouteErrorBoundary'

const LandingPage = React.lazy(() => import('../pages/landing'))
const SupportPage = React.lazy(() => import('../pages/landing/Support'))
const PrivacyPolicyPage = React.lazy(
  () => import('../pages/landing/PrivacyPolicy')
)
const TermsAndConditionsPage = React.lazy(
  () => import('../pages/landing/TermsAndConditions')
)
const VehicleDetailsSearchPage = React.lazy(
  () => import('../pages/vehicle/vehicleDetailsSearch/VehicleDetailsSearch')
)
const VehicleDetailsPage = React.lazy(
  () => import('../pages/vehicle/vehicleDetails/VehicleDetails')
)
const LoginPage = React.lazy(() => import('../pages/login/Login'))
const GetStartedPage = React.lazy(() => import('../pages/register/GetStarted'))
const RegisterPage = React.lazy(() => import('../pages/register/Register'))
const ForgotPasswordPage = React.lazy(
  () => import('../pages/forgot-password/ForgotPassword')
)
const ResetPasswordPage = React.lazy(
  () => import('../pages/reset-password/ResetPassword')
)
const ActivateAccountPage = React.lazy(
  () => import('../pages/activate-account/ActivateAccount')
)
const InsuranceProductsPage = React.lazy(
  () => import('../pages/insuranceProduct/insuranceProductSelection')
)
const DashboardSwitcher = React.lazy(
  () => import('../pages/dashboard/DashboardSwitcher')
)
const UserQuotesPage = React.lazy(() => import('../pages/users/userQuotes'))
const QuoteDetailPage = React.lazy(
  () => import('../pages/users/userQuotes/QuoteDetail')
)
const UserPoliciesPage = React.lazy(() => import('../pages/users/userPolicies'))
const UsersPage = React.lazy(() => import('../pages/users/Users'))
const UserDetailsPage = React.lazy(() => import('../pages/users/userDetails'))
const PaymentsPage = React.lazy(() => import('../pages/payments'))
const CategoriesPage = React.lazy(() => import('../pages/insuranceProduct'))
const CoverageOptionsPage = React.lazy(() => import('../pages/coverage'))
const PlansPage = React.lazy(() => import('../pages/plan/planList/PlanList'))
const VehiclesPage = React.lazy(
  () => import('../pages/vehicle/vehicleList/VehicleList')
)
const CoverageBenefitsPage = React.lazy(
  () => import('../pages/coverageBenefit')
)
const CoverageRatesPage = React.lazy(() => import('../pages/coverageRate'))
const CoverageSelectionPage = React.lazy(
  () => import('../pages/coverage/coverageTypeSelection')
)
const PlanSelectionPage = React.lazy(
  () => import('../pages/plan/planSelection')
)
const PaymentCheckoutPage = React.lazy(
  () => import('../pages/payments/checkout/PaymentCheckout')
)
const PaymentMethodPage = React.lazy(
  () => import('../pages/payments/paymentMethod/PaymentMethodPage')
)
const NgaoPaymentPage = React.lazy(
  () => import('../pages/payments/ngaoPayment/NgaoPaymentPage')
)
const UserEmiPage = React.lazy(() => import('../pages/users/userEmi'))
const AgentQuotesPage = React.lazy(() => import('../pages/users/userQuotes'))
const UserKycPage = React.lazy(() =>
  import('../pages/users/userKyc').then((m) => ({ default: m.UserKycPage }))
)
const CustomerListPage = React.lazy(() => import('../pages/agents/customers'))
const AgentPoliciesPage = React.lazy(
  () => import('../pages/users/userPolicies')
)
const UserProfilePage = React.lazy(() => import('../pages/users/userProfile'))
const AgentProfilePage = React.lazy(
  () => import('../pages/agents/agentProfile')
)
const PrivacySettingsPage = React.lazy(
  () => import('../pages/settings/privacySettings')
)
const AgentPayoutsPage = React.lazy(() => import('../pages/agents/payouts'))
const AdminPayoutRequestsPage = React.lazy(
  () => import('../pages/admin/payoutRequests')
)
const AdminAccountStatusPage = React.lazy(
  () => import('../pages/admin/accountStatus')
)
const AdminAuditHistoryPage = React.lazy(
  () => import('../pages/admin/auditHistory')
)
const AdminIpBlocksPage = React.lazy(() => import('../pages/admin/ipBlocks'))
const AdminLoginAttemptsPage = React.lazy(
  () => import('../pages/admin/loginAttempts')
)
const AdminRequestJournalPage = React.lazy(
  () =>
    import(
      '../pages/admin/requestJournal/requestJournalList/requestJournalList'
    )
)
const AdminEnquiriesPage = React.lazy(() => import('../pages/admin/enquiries'))
const AdminSettingsPage = React.lazy(() => import('../pages/admin/settings'))
const UnderwritersPage = React.lazy(() => import('../pages/underwriter'))
const KycManagementPage = React.lazy(() => import('../pages/kycManagement'))
const LogbookManagementPage = React.lazy(
  () => import('../pages/logbookManagement')
)

const appRouter = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/',
        element: <LandingPage />
      },
      {
        path: '/login',
        element: <LoginPage />
      },
      {
        path: '/login/mfa-setup',
        element: <LoginPage />
      },
      {
        path: '/login/mfa-verify',
        element: <LoginPage />
      },
      {
        path: '/register',
        element: (
          <GuestGuard>
            <RegisterPage />
          </GuestGuard>
        )
      },
      {
        path: '/forgot-password',
        element: (
          <GuestGuard>
            <ForgotPasswordPage />
          </GuestGuard>
        )
      },
      {
        path: '/reset-password',
        element: (
          <GuestGuard>
            <ResetPasswordPage />
          </GuestGuard>
        )
      },
      {
        path: '/activate',
        element: (
          <GuestGuard>
            <ActivateAccountPage />
          </GuestGuard>
        )
      },
      {
        path: '/logout',
        element: <Logout />
      },
      {
        path: '/support',
        element: <SupportPage />
      },
      {
        path: '/privacy-policy',
        element: <PrivacyPolicyPage />
      },
      {
        path: '/terms-conditions',
        element: <TermsAndConditionsPage />
      },
      {
        path: '/insurance-products',
        element: <InsuranceProductsPage />
      },
      {
        path: '/insurance-products/:categoryId/vehicle',
        element: <VehicleDetailsSearchPage />
      },
      {
        path: '/insurance-products/:categoryId/vehicle/details',
        element: <VehicleDetailsPage />
      },
      {
        path: '/insurance-products/:categoryId/coverage-options',
        element: <CoverageSelectionPage />
      },
      {
        path: '/insurance-products/:categoryId/coverage-options/:coverageTypeId/plans',
        element: <PlanSelectionPage />
      },
      {
        path: '/insurance-products/:categoryId/get-started',
        element: (
          <GuestGuard>
            <GetStartedPage />
          </GuestGuard>
        )
      },
      {
        path: '/insurance-products/:categoryId/coverage-options/:coverageTypeId/checkout',
        element: (
          <AuthGuard>
            <PaymentCheckoutPage />
          </AuthGuard>
        )
      },
      {
        path: '/insurance-products/:categoryId/coverage-options/:coverageTypeId/payment-method',
        element: (
          <AuthGuard>
            <PaymentMethodPage />
          </AuthGuard>
        )
      },
      {
        path: '/insurance-products/:categoryId/coverage-options/:coverageTypeId/ngao',
        element: (
          <AuthGuard>
            <NgaoPaymentPage />
          </AuthGuard>
        )
      },
      {
        path: '/user/kyc',
        element: (
          <AuthGuard>
            <RouteGuard
              requiredRoles={[
                ROLES_TYPE.USER,
                ROLES_TYPE.AGENT,
                ROLES_TYPE.ADMIN
              ]}
            >
              <UserKycPage />
            </RouteGuard>
          </AuthGuard>
        )
      },
      {
        element: (
          <AuthGuard>
            <AuthLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: '/dashboard',
            element: <DashboardSwitcher />
          },
          {
            path: '/user/quotes',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.USER, ROLES_TYPE.AGENT]}>
                <UserQuotesPage />
              </RouteGuard>
            )
          },
          {
            path: '/user/quotes/:quoteId',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.USER, ROLES_TYPE.AGENT]}>
                <QuoteDetailPage />
              </RouteGuard>
            )
          },

          {
            path: '/user/policies',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.USER]}>
                <UserPoliciesPage />
              </RouteGuard>
            )
          },
          {
            path: '/user/payments',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.USER]}>
                <PaymentsPage />
              </RouteGuard>
            )
          },
          {
            path: '/user/emi',
            element: (
              <RouteGuard
                requiredRoles={[
                  ROLES_TYPE.USER,
                  ROLES_TYPE.AGENT,
                  ROLES_TYPE.ADMIN,
                  ROLES_TYPE.SUPERADMIN
                ]}
              >
                <UserEmiPage />
              </RouteGuard>
            )
          },
          {
            path: '/user/profile',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.USER]}>
                <UserProfilePage />
              </RouteGuard>
            )
          },
          {
            path: '/user/payments/checkout',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.USER]}>
                <PaymentCheckoutPage />
              </RouteGuard>
            )
          },
          {
            path: '/agent/customers',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.AGENT]}>
                <CustomerListPage />
              </RouteGuard>
            )
          },
          {
            path: '/agent/quotes',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.AGENT]}>
                <AgentQuotesPage />
              </RouteGuard>
            )
          },
          {
            path: '/agent/policies',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.AGENT]}>
                <AgentPoliciesPage />
              </RouteGuard>
            )
          },
          {
            path: '/agent/payments',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.AGENT]}>
                <PaymentsPage />
              </RouteGuard>
            )
          },
          {
            path: '/agent/payouts',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.AGENT]}>
                <AgentPayoutsPage />
              </RouteGuard>
            )
          },
          {
            path: '/agent/profile',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.AGENT]}>
                <AgentProfilePage />
              </RouteGuard>
            )
          },
          {
            path: '/setting/privacy',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.USER, ROLES_TYPE.AGENT]}>
                <PrivacySettingsPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/account-status',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <AdminAccountStatusPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/users',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <UsersPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/users/:userId',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <UserDetailsPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/quotes',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <UserQuotesPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/payments',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <PaymentsPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/policies',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <UserPoliciesPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/insurance-products',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <CategoriesPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/coverage-options',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <CoverageOptionsPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/coverage-benefits',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <CoverageBenefitsPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/coverage-rates',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <CoverageRatesPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/plans',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <PlansPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/underwriters',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <UnderwritersPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/payout-requests',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <AdminPayoutRequestsPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/enquiries',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <AdminEnquiriesPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/vehicles',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <VehiclesPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/kyc',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <KycManagementPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/logbooks',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <LogbookManagementPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/audit-history',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.SUPERADMIN]}>
                <AdminAuditHistoryPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/ip-blocks',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.SUPERADMIN]}>
                <AdminIpBlocksPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/login-attempts',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.SUPERADMIN]}>
                <AdminLoginAttemptsPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/request-journal',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.SUPERADMIN]}>
                <AdminRequestJournalPage />
              </RouteGuard>
            )
          },
          {
            path: '/admin/settings',
            element: (
              <RouteGuard
                requiredRoles={[ROLES_TYPE.ADMIN, ROLES_TYPE.SUPERADMIN]}
              >
                <AdminSettingsPage />
              </RouteGuard>
            )
          },
          {
            path: '/agent/kyc',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.AGENT]}>
                <KycManagementPage />
              </RouteGuard>
            )
          },
          {
            path: '/agent/logbooks',
            element: (
              <RouteGuard requiredRoles={[ROLES_TYPE.AGENT]}>
                <LogbookManagementPage />
              </RouteGuard>
            )
          }
        ]
      },
      {
        path: '*',
        element: <Navigate to="/" replace />
      }
    ]
  }
])

export default appRouter
